import { useSEO } from "@/components/SEO";
import { Eyebrow, Masked, Reveal, ArrowLink } from "@/components/bits";
import XRayScanner from "@/components/XRayScanner";
import OpportunityMap from "@/components/OpportunityMap";
import CTASection from "@/components/CTASection";
import { SEO, XRAY_CATEGORIES } from "@/lib/site";

const BusinessXRay = () => {
    useSEO({ title: SEO["/business-x-ray"].title, description: SEO["/business-x-ray"].description, path: "/business-x-ray" });

    return (
        <>
            <section className="relative overflow-hidden bg-ink pt-32 pb-24 text-ivory sm:pt-40 sm:pb-32" data-testid="xray-hero">
                <div className="bg-grid-dark absolute inset-0" aria-hidden="true" />
                <div className="container-x relative">
                    <Eyebrow dark>The Business X-Ray</Eyebrow>
                    <Masked
                        lines={["Before You Spend More to Grow,", "Find Out What Your Business Is", "Already Losing — and Overpaying For."]}
                        className="mt-7 max-w-5xl font-display text-4xl font-extrabold leading-[1.06] tracking-tightest sm:text-6xl lg:text-[68px]"
                    />
                    <p className="mt-8 max-w-2xl text-lg leading-relaxed text-cloud sm:text-xl">
                        The Vantera Business X-Ray is a diagnostic examination of the company's revenue engine and operating economics. It gives leadership a prioritized view of where revenue can be created or recovered, where costs may be reduced, and where margin can improve.
                    </p>
                    <div className="mt-16">
                        <XRayScanner />
                    </div>
                </div>
            </section>

            <section className="py-24 sm:py-32" data-testid="xray-map">
                <div className="container-x">
                    <Reveal>
                        <Eyebrow>The output</Eyebrow>
                    </Reveal>
                    <div className="mt-10">
                        <OpportunityMap />
                    </div>
                </div>
            </section>

            <section className="border-t border-ink/10 bg-paper py-24 sm:py-32" data-testid="xray-components">
                <div className="container-x">
                    <div className="max-w-3xl">
                        <Eyebrow>What we examine</Eyebrow>
                        <Masked inview lines={["Nine Diagnostic Instruments.", "One Economic Picture."]} className="mt-7 font-display text-4xl font-extrabold tracking-tightest sm:text-5xl" />
                    </div>
                    <div className="mt-14">
                        {XRAY_CATEGORIES.map((c, i) => (
                            <Reveal key={c.num} delay={i * 0.04}>
                                <div className="group grid gap-3 border-t border-ink/10 py-7 transition-colors duration-300 hover:bg-white sm:grid-cols-[90px_1fr_1.4fr] sm:gap-8 sm:px-4" data-testid={`xray-component-${c.num}`}>
                                    <span className="font-mono text-xs tracking-[0.2em] text-vantera">{c.num}</span>
                                    <h3 className="font-display text-xl font-extrabold tracking-tight transition-colors duration-300 group-hover:text-vantera sm:text-2xl">{c.name}</h3>
                                    <p className="text-[15.5px] leading-relaxed text-slategray">{c.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                        <div className="border-t border-ink/10" />
                    </div>
                </div>
            </section>

            <section className="relative overflow-hidden bg-ink py-20 text-ivory sm:py-24" data-testid="xray-output">
                <div className="bg-grid-dark absolute inset-0" aria-hidden="true" />
                <div className="container-x relative grid items-center gap-10 lg:grid-cols-2">
                    <div>
                        <Eyebrow dark>Not a generic consulting report</Eyebrow>
                        <Masked inview lines={["A Clear Profit", "Opportunity Map."]} className="mt-6 font-display text-4xl font-extrabold tracking-tightest sm:text-5xl" />
                    </div>
                    <Reveal delay={0.15}>
                        <p className="text-lg leading-relaxed text-cloud">
                            Leadership receives a prioritized plan showing where revenue is leaking, where costs can potentially be reduced, which opportunities matter most, and what should happen first.
                        </p>
                        <div className="mt-8">
                            <ArrowLink to="/contact" dark testid="xray-output-link">Request My Business X-Ray</ArrowLink>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CTASection heading={["See the Business", "Differently."]} sub="Find what's hiding inside the business you already have." primary="Request My Business X-Ray" secondary="Book an Executive Review" testid="xray-final-cta" />
        </>
    );
};

export default BusinessXRay;